/**
 * The SLA stamp.
 *
 * It renders three facts and one explanation, and the explanation is not decoration:
 * an operator looking at a live investigation whose deadline is in the past will
 * assume the clock restarted when the incident reopened, and that assumption is
 * invisible while the screen is silent.
 *
 * ── Why this is a printed figure and not a `StatusNote` ─────────────────────
 *
 * A note is a state that **arrived** — "this read is late", "you are not allowed to
 * see this" — and it is drawn as a banner because it interrupts. A deadline is not a
 * state that arrived: it is a property of the incident, on screen every single time
 * the incident is, and a banner that is always there stops being read within a week.
 *
 * So it is printed instead, the way a received-date stamp is printed on a document:
 * an 11px key, the reading in tabular numerals at title size, and underneath it the
 * instant the reading was measured against — the figure's own provenance, which is
 * the thing that stops a re-grade being mistaken for a fresh budget. It tints — wash
 * plus a 1px border on all four sides, never an edge stripe — in exactly the two
 * cases where the reading is something somebody has to act on: the deadline was
 * missed, or the incident concluded after it.
 *
 * `data-severity` survives the change, because that attribute is what
 * `incidents.test.tsx` and the greyscale acceptance test read the meaning off, and
 * what the tint is selected by.
 *
 * ── What it shows for a breached-then-reopened incident ─────────────────────
 *
 * **"Quá hạn", measured from the original `sla_due_at`, with no fresh budget
 * anywhere on the panel** — and, because `running` is true, the sentence saying in as
 * many words that a reopen keeps the original deadline and the breach.
 *
 * That is what the server does: a reopen (`resolved`/`false_positive` →
 * `investigating`) keeps `sla_due_at` and keeps `sla_breached_at` if it is set, and
 * clears only the conclusion. An owner who could resolve-and-reopen for a fresh SLA
 * would have a button that turns a missed deadline into a met one. So there is no
 * branch in this component that produces a countdown for a breached incident: the
 * `breached` case of `SlaState` carries `overdueMs` and no remaining time at all, and
 * the shape of the union is what makes rendering one impossible.
 *
 * ── Three absences, kept apart ──────────────────────────────────────────────
 *
 *   `sla_due_at: null`    →  *this severity has no deadline in this company's policy*
 *   deadline, not breached →  the deadline and what is left
 *   `sla_breached: true`   →  the deadline, **quá hạn**, and by how much
 *
 * The first is never rendered as "met" and never as a budget of zero. An explicit
 * `null` in `incident_sla_hours_json` means *this severity has no deadline* and is
 * kept apart from an absent key, which means *the company did not say* — the server
 * resolves both to `sla_due_at: null`, so this panel says the honest thing it can
 * say, which is that no deadline was published.
 *
 * ── The panel does not do the arithmetic ────────────────────────────────────
 *
 * `sla_breached` decides whether the deadline was missed. Comparing timestamps here
 * would report a live incident as overdue on a laptop with a fast clock — the same
 * reason the token list on `/devices` prints the server's expiry rather than
 * computing it. `nowMs` is used only to say *how far*, which is a quantity nobody
 * acts on by itself, and `splitDuration` returns `null` rather than zero when the
 * arithmetic has nothing to say.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { describeSla, splitDuration } from '../../incidents/sla';
import type { IncidentState } from '../../incidents/state-machine';
import { Icon, SEVERITY_ICON, type Severity } from '../../states/icons';

export interface SlaPanelProps {
  readonly slaDueAt: string | null;
  readonly slaBreached: boolean;
  readonly state: IncidentState;
  readonly timezone: string;
  /** Injected so the panel has no clock of its own — the tests set it. */
  readonly nowMs: number;
}

/** The stamp the three branches share: key, reading, provenance. */
function SlaStamp({
  severity,
  title,
  role = 'status',
  children,
}: {
  readonly severity: Severity;
  readonly title: string;
  readonly role?: 'status' | 'alert';
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className="wp-c-sla" data-severity={severity} data-testid="l5-sla" role={role}>
      {/* Glyph, word, then colour — DESIGN.md §4, the same triple the badges carry,
          so the stamp survives a greyscale screenshot. */}
      <p className="wp-c-sla__key">
        <Icon name={SEVERITY_ICON[severity]} className="wp-c-sla__icon" />
        {title}
      </p>
      {children}
    </section>
  );
}

export function SlaPanel({
  slaDueAt,
  slaBreached,
  state,
  timezone,
  nowMs,
}: SlaPanelProps): ReactNode {
  const { t, locale } = useI18n();
  const sla = describeSla({ slaDueAt, slaBreached, state }, nowMs);

  if (sla.kind === 'none') {
    return (
      <SlaStamp severity="info" title={t('incidents.sla.none.title')}>
        <p className="wp-c-sla__figure" data-testid="l5-sla-none">
          {t('incidents.sla.none.body')}
        </p>
      </SlaStamp>
    );
  }

  const due = formatInstant(sla.dueAt, locale, timezone);
  const span = splitDuration(sla.kind === 'breached' ? sla.overdueMs : sla.remainingMs);
  const interval =
    span === null
      ? // Not "0 phút". An unmeasurable distance and a distance of zero are two
        // different statements, and this build has shipped the second in place of the
        // first once already (D-22).
        t('incidents.sla.interval.unknown')
      : t('incidents.sla.interval', { hours: span.hours, minutes: span.minutes });

  if (sla.kind === 'breached') {
    return (
      <SlaStamp severity="critical" role="alert" title={t('incidents.sla.breached.title')}>
        {/* The reading first, then what it was measured against. */}
        <p className="wp-c-sla__figure" data-testid="l5-sla-overdue">
          {t('incidents.sla.overdue', { interval })}
        </p>
        <p className="wp-c-sla__stale" data-testid="l5-sla-due">
          {t('incidents.sla.due', { when: due })}
        </p>
        {sla.running ? (
          <p className="wp-c-sla__stale" data-testid="l5-sla-not-restarted">
            {t('incidents.sla.notRestarted')}
          </p>
        ) : null}
        <p className="wp-c-sla__stale" data-testid="l5-sla-arithmetic">
          {t('incidents.sla.arithmetic')}
        </p>
      </SlaStamp>
    );
  }

  const late = sla.remainingMs < 0;
  return (
    <SlaStamp
      severity={late ? 'medium' : 'low'}
      title={t(late ? 'incidents.sla.concludedLate.title' : 'incidents.sla.due.title')}
    >
      <p className="wp-c-sla__figure" data-testid="l5-sla-remaining">
        {/* A concluding incident whose deadline passed *after* it concluded is not a
            breach — the server does not count it as one — so it is rendered as
            "closed after the deadline" rather than as overdue. Two different facts,
            two different sentences. */}
        {late
          ? t('incidents.sla.concludedLate', { interval })
          : t('incidents.sla.remaining', { interval })}
      </p>
      <p className="wp-c-sla__stale" data-testid="l5-sla-due">
        {t('incidents.sla.due', { when: due })}
      </p>
      <p className="wp-c-sla__stale" data-testid="l5-sla-arithmetic">
        {t('incidents.sla.arithmetic')}
      </p>
    </SlaStamp>
  );
}
